import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { DataStateChangeEvent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { Router } from '@angular/router';
import { IKeyPairValue } from '../interface/IKeyValuePair';
import { Common } from 'src/app/common/common';
import { CompositeFilterDescriptor, process, State, filterBy, SortDescriptor } from '@progress/kendo-data-query';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { forkJoin, Subject } from 'rxjs';
import { CommonService } from '../service/common/common.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';

const flatten = filter => {
  const filters = (filter || {}).filters;
  if (filters) {
    return filters.reduce((acc, curr) => acc.concat(curr.filters ? flatten(curr) : [curr]), []);
  }
  return [];
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})

export class DashboardComponent implements OnInit {

  loginDisplay = false;
  displayedColumns: string[] = ['claim', 'value'];
  dataSource: any = [];
  claims: any;
  activeAccount: any;

  private readonly _destroying$ = new Subject<void>();
  constructor(private router: Router, private commonService: CommonService,
              private authService: MsalService,
              private msalBroadcastService: MsalBroadcastService,
              private formBuilder?: FormBuilder) {}

  @ViewChild('searchbox') searchbox: ElementRef;

  public gridData: GridDataResult;
  public dialogRef: DialogRef;
  public dashBoardForm: FormGroup;
  public agreementList: any[] = [];
  public mySelection: string[] = [];
  public countryList: IKeyPairValue[] = [];
  public countryOriginalList: IKeyPairValue[] = [];
  public donorAgencyList: IKeyPairValue[] = [];
  public donorAgencyOriginalList: IKeyPairValue[] = [];
  public agreementStatusList: IKeyPairValue[] = [];
  public agreementStatusOriginalList: IKeyPairValue[] = [];
  public activityList: IKeyPairValue[] = [];
  public activityOriginalList: IKeyPairValue[] = [];
  public technicalAreaList: IKeyPairValue[] = [];
  public technicalAreaOriginalLis: IKeyPairValue[] = [];
  public contactList: IKeyPairValue[] = [];
  public contactOriginalList: IKeyPairValue[] = [];
  public pageSize = 10;
  public skip = 0;
  public typedg: 'numeric' | 'input' = 'numeric';
  public buttonCount = 5;
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public text: string = '';
  public enteredSearchText: string = '';
  public reqstate: State = {
    skip: 0,
    take: this.pageSize
  };
  public selectedCountry: string = '';
  public filter: CompositeFilterDescriptor;
  public filteredData: any = [];
  public allUsersOriginalList: IKeyPairValue[] = [];
  public allUsersList: IKeyPairValue[] = [];
  public techORFilter: CompositeFilterDescriptor;
  public countryORFilter: CompositeFilterDescriptor;
  public technicalAreaFilterList: any[] = [];
  public countryFilterList: any[] = [];
  public techFilterList = [];
  public conFilterList = [];
  public filterList = [];
  public isAdminCheck: boolean = false;
  public sort: SortDescriptor[] = [];
  // multi filter
  filterConditions: any = {};

  public sortChange(sort: SortDescriptor[]): void {
    if (sort && sort.length > 0) {
      this.sort = sort;
      if (this.filteredData.length !== 0) {
        this.gridData = process(this.filteredData, this.reqstate);
      } else {
        this.gridData = {
          data: this.agreementList.sort(this.sortByColumn(this.sort)),
          total: this.agreementList.length
        };
        this.gridData = process(this.agreementList, this.reqstate);
      }
    }
  }
  public sortByColumn(sort: Array<SortDescriptor>) {
    const property = sort[0].field;
    const dir = (sort[0].dir === undefined) ? 'asc' : 'desc';
    const sortOrder = (dir === 'asc' ? 1 : -1);
    return function (a, b) {
      const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
      return result * sortOrder;
    };
  }

  ngOnInit() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.activeAccount = payload.account;
        this.authService.instance.setActiveAccount(this.activeAccount);
        this.claims = payload.account.idTokenClaims;
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        this.claims = this.authService.instance.getActiveAccount()?.idTokenClaims;
        if (this.claims && this.claims.roles) {
          this.getClaims(this.authService.instance.getActiveAccount()?.idTokenClaims);
          Common.loader('show');
          this.loadValues();
          this.getAgreements();
        } else {
          if (this.claims) {
            this.router.navigate(['authorized-error']);
          }
        }
      });
    this.initializeFormControls();
  }

  initializeFormControls() {
    this.dashBoardForm = this.formBuilder.group({
      country: new FormControl(''),
      status: new FormControl(''),
      awardMaintainer: new FormControl(''),
      donorAgency: new FormControl(''),
      technicalArea: new FormControl(''),
      title: new FormControl(''),
      awardNumber: new FormControl(''),
      awardNickName: new FormControl('')
    });
  }

  loadValues() {
    forkJoin([
      this.commonService.getCountry(),
      this.commonService.getDonorAgency(),
      this.commonService.getAgreementStatus(),
      this.commonService.getActivity(),
      this.commonService.getTechnicalArea(),
      this.commonService.getAllUsers(),
      this.commonService.getAdminCheck(),
    ]).subscribe(([resp1, resp2, resp3, resp4, resp5, resp6, res7]) => {
      this.countryOriginalList = resp1;
      this.countryList = this.countryOriginalList.slice();
      this.donorAgencyOriginalList = resp2;
      this.donorAgencyList = this.donorAgencyOriginalList.slice();
      this.agreementStatusOriginalList = resp3;
      this.agreementStatusList = this.agreementStatusOriginalList.slice();
      this.activityOriginalList = resp4;
      this.activityList = this.activityOriginalList.slice();
      this.technicalAreaOriginalLis = resp5;
      this.technicalAreaList = this.technicalAreaOriginalLis.slice();
      this.allUsersOriginalList = resp6;
      this.allUsersList = this.allUsersOriginalList.slice();
      this.isAdminCheck = res7;
    });
  }

  // Get Agreement Lists
  getAgreements() {
    this.commonService.getAgreements().subscribe((response) => {
      if (response.length > 0) {
        this.agreementList = response;
        this.gridData = {
          data: this.agreementList.slice(this.skip, this.skip + this.pageSize),
          total: this.agreementList.length
        };
        this.toSetFilterActive();
      }
      Common.loader('hide');
    });
  }

  toSetFilterActive() {
    this.dashBoardForm.patchValue({ status: 1 });
    const defaultStatusValue: any = {};
    defaultStatusValue.statusName = 'Active';
    this.awardStatusSelectionChange(defaultStatusValue);
  }


  // Filter changes start
  public handleCountryFilter(value: any) {
    if (value.length >= 1) {
      this.countryList = this.countryOriginalList.filter((s) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1);
    } else {
      this.countryList = this.countryOriginalList;
    }
  }

  public handleAwardStatus(value: any) {
    if (value.length >= 1) {
      // tslint:disable-next-line: max-line-length
      this.agreementStatusList = this.agreementStatusOriginalList.filter((s) => s.statusName.toLowerCase().indexOf(value.toLowerCase()) !== -1);
    } else {
      this.agreementStatusList = this.agreementStatusOriginalList;
    }
  }

  public handleAwardManager(value: any) {
    if (value.length >= 1) {
      this.contactList = this.contactOriginalList.filter((s) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1);
    } else {
      this.contactList = this.contactOriginalList;
    }
  }

  public handleDonorAgency(value: any) {
    if (value.length >= 1) {
      this.donorAgencyList = this.donorAgencyOriginalList.filter((s) => s.agencyName.toLowerCase().indexOf(value.toLowerCase()) !== -1);
    } else {
      this.donorAgencyList = this.donorAgencyOriginalList;
    }
  }

  public handleEGPAFAwardManager(value: any) {
    if (value.length >= 1) {
      this.allUsersList = this.allUsersOriginalList.filter((s) => s.displayName.toLowerCase().indexOf(value.toLowerCase()) !== -1);
    } else {
      this.allUsersList = this.allUsersOriginalList;
    }
  }

  public handleTechnicalAreas(value: any) {
    if (value.length >= 1) {
      this.technicalAreaList = this.technicalAreaOriginalLis.filter((s) => s.description.toLowerCase().indexOf(value.toLowerCase()) !== -1);
    } else {
      this.technicalAreaList = this.technicalAreaOriginalLis;
    }
  }
  // Filter changes end

  onLoadServers(id: number) {
    this.router.navigate(['/servers', id, 'edit'], { queryParams: { allowedit: id }, fragment: 'loading' });
  }

  pageChange({ skip, take }: PageChangeEvent): void {
    this.skip = skip;
    this.pageSize = take;
  }

  filterChange(filters: CompositeFilterDescriptor): void {
    this.filter = filters;
    const filterData = filterBy(this.agreementList, filters);
    this.gridData = process(filterData, this.reqstate);
  }

  dataStateChange(state: DataStateChangeEvent): void {
    this.reqstate = state;
    if (this.filteredData.length > 0) {
      this.gridData = process(this.filteredData, this.reqstate);
    } else {
      this.gridData = process(this.agreementList, this.reqstate);
    }
  }

  onLogIn() {
    // this.authService.login();
  }

  onLogOut() {
    // this.authService.logout();
  }

  countrySelectionChange(value: any): void {
    this.countryFilterList = [];
    if (value.length > 0) {
      value.forEach(element => {
        this.countryFilterList.push(element);
      });
    } else {
      this.conFilterList = [];
      this.countryORFilter = undefined;
    }
    this.onSearchClick();
  }

  awardStatusSelectionChange(value: any): void {
    if (value) {
      this.filterConditions.StatusName = value.statusName;
    } else {
      this.filterConditions.StatusName = '';
    }
    this.onSearchClick();
  }

  awardManagerSelectionChange(value: any): void {
    if (value) {
      this.filterConditions.AwardManager = value.displayName;
    } else {
      this.filterConditions.AwardManager = '';
    }
    this.onSearchClick();
  }

  donorAgencySelectionChange(value: any): void {
    if (value) {
      this.filterConditions.AgencyName = value.agencyName;
    } else {
      this.filterConditions.AgencyName = '';
    }
    this.onSearchClick();
  }

  technicalAreaSelectionChange(value: any): void {
    this.technicalAreaFilterList = [];
    if (value.length > 0) {
      value.forEach(element => {
        this.technicalAreaFilterList.push(element);
      });
    } else {
      this.techFilterList = [];
      this.techORFilter = undefined;
    }
    this.onSearchClick();
  }

  titleChange(event: any) {
    if (event.target.value !== '') {
      this.filterConditions.Title = event.target.value;
    } else {
      this.filterConditions.Title = '';
    }
    this.onSearchClick();
  }

  awardNumberChange(event: any) {
    if (event.target.value !== '') {
      this.filterConditions.EGPAFAwardNumber = event.target.value;
    } else {
      this.filterConditions.EGPAFAwardNumber = '';
    }
    this.onSearchClick();
  }

  awardNickNameChange(event: any) {
    if (event.target.value !== '') {
      this.filterConditions.NickName = event.target.value;
    } else {
      this.filterConditions.NickName = '';
    }
    this.onSearchClick();
  }

  onRemoveCountry(event: any) {
    if (event.dataItem) {
      const index = this.countryFilterList.findIndex(({ id }) => id === event.dataItem.name);
      this.countryFilterList.splice(index, 1);
      if (this.countryFilterList.length === 0) {
        this.onSearchClick();
      }
    }
  }

  onRemoveTechnicalArea(event: any) {
    if (event.dataItem) {
      const index = this.technicalAreaFilterList.findIndex(({ id }) => id === event.dataItem.description);
      this.technicalAreaFilterList.splice(index, 1);
      if (this.technicalAreaFilterList.length === 0) {
        this.onSearchClick();
      }
    }
  }

  onClearAll() {
    this.dashBoardForm.patchValue({ country: '' });
    this.dashBoardForm.patchValue({ status: '' });
    this.dashBoardForm.patchValue({ awardMaintainer: '' });
    this.dashBoardForm.patchValue({ donorAgency: '' });
    this.dashBoardForm.patchValue({ technicalArea: '' });
    this.dashBoardForm.patchValue({ title: '' });
    this.dashBoardForm.patchValue({ awardNumber: '' });
    this.dashBoardForm.patchValue({ awardNickName: '' });
    this.filterConditions.StatusName = '';
    this.filterConditions.AwardManager = '';
    this.filterConditions.AgencyName = '';
    this.filterConditions.Title = '';
    this.filterConditions.EGPAFAwardNumber = '';
    this.filterConditions.NickName = '';
    this.technicalAreaFilterList = [];
    this.countryFilterList = [];
    this.conFilterList = [];
    this.techFilterList = [];
    this.countryORFilter = undefined;
    this.techORFilter = undefined;
    this.onSearchClick();
  }

  onSearchClick() {
    let allSearchAndFilter: CompositeFilterDescriptor;
    if (this.filterConditions) {
      allSearchAndFilter = { logic: 'and', filters: [{ field: 'title', operator: 'contains', value: '' }] };
      if (this.filterConditions.StatusName) {
        allSearchAndFilter.filters.push({ field: 'StatusName', operator: 'contains', value: this.filterConditions.StatusName });
      }
      if (this.filterConditions.AwardManager) {
        allSearchAndFilter.filters.push({ field: 'AwardManager', operator: 'contains', value: this.filterConditions.AwardManager });
      }
      if (this.filterConditions.AgencyName) {
        allSearchAndFilter.filters.push({ field: 'AgencyName', operator: 'contains', value: this.filterConditions.AgencyName });
      }
      if (this.filterConditions.Title) {
        allSearchAndFilter.filters.push({ field: 'Title', operator: 'contains', value: this.filterConditions.Title });
      }
      if (this.filterConditions.EGPAFAwardNumber) {
        allSearchAndFilter.filters.push({ field: 'EGPAFAwardNumber', operator: 'contains', value: this.filterConditions.EGPAFAwardNumber });
      }
      if (this.filterConditions.NickName) {
        allSearchAndFilter.filters.push({ field: 'NickName', operator: 'contains', value: this.filterConditions.NickName });
      }
      if (this.technicalAreaFilterList.length > 0) {
        this.techFilterList = [];
        this.technicalAreaFilterList.forEach(element => {
          const obj: any = {};
          obj.field = 'TechnicalAreas';
          obj.operator = 'contains';
          obj.value = element;
          this.techFilterList.push(obj);
        });
      }
      if (this.countryFilterList.length > 0) {
        this.conFilterList = [];
        this.countryFilterList.forEach(element => {
          const obj: any = {};
          obj.field = 'CountryNames';
          obj.operator = 'contains';
          obj.value = element;
          this.conFilterList.push(obj);
        });
      }
      if (this.techFilterList.length > 0) {
        this.techORFilter = { logic: 'or', filters: this.techFilterList };
      }
      if (this.conFilterList.length > 0) {
        this.countryORFilter = { logic: 'or', filters: this.conFilterList };
      }

      if (allSearchAndFilter.filters.length > 0) {
        this.filteredData = filterBy(this.agreementList, allSearchAndFilter);
        if (this.techORFilter && this.techORFilter.filters.length > 0) {
          this.filteredData = filterBy(this.filteredData, this.techORFilter);
        }
        if (this.countryORFilter && this.countryORFilter.filters.length > 0) {
          if (this.countryFilterList[0] === 'Multi-Country') {
            const list: any[] = [];
            this.agreementList.forEach((elem: any) => {
              let splitList: any[];
              if (elem.CountryNames !== null) {
                splitList = elem.CountryNames.split(',');
                if (splitList.length > 1) {
                  list.push(elem);
                } else {
                  if (splitList[0].trim() === 'Multi-Country') {
                    list.push(elem);
                  }
                }
              }
            });
            this.filteredData = filterBy(list, allSearchAndFilter);
          } else {
            this.filteredData = filterBy(this.filteredData, this.countryORFilter);
          }
        }
      }
      this.skip = 0;
      this.gridData = {
        data: this.filteredData.slice(this.skip, this.skip + this.pageSize),
        total: this.filteredData.length
      };
    }
  }

  addUserRouting() {
    const url = 'https://portal.azure.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Users/objectId/02977f3d-9b2b-4598-bc00-724f4cf9128f/appId/b9b11b4c-9547-49f1-9f2e-fa63b8ec5120/preferredSingleSignOnMode~';
    window.open(url);
  }

  // To prevent Enter
  prevent(event) {
    event.preventDefault();
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  getClaims(claims: any) {
    this.dataSource = [
      { id: 1, claim: 'Display Name', value: claims ? claims['name'] : null },
      { id: 2, claim: 'User Principal Name (UPN)', value: claims ? claims['preferred_username'] : null },
      { id: 2, claim: 'OID', value: claims ? claims['oid'] : null }
    ];
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
